/**
 * StylizedNumberedListEditor - Editor component for the shared numbered/bulleted list block
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';

import type { JSONContent } from '@tiptap/core';
import {
  createStylizedListStyles,
  useIsMobileViewport,
} from '@course-authoring/ui-blocks';
import {
  useTheme,
  type NumberedListItem,
  type StylizedListTitleLevel,
  type StylizedNumberedListBlock,
} from '@course-authoring/shared-types';

import { StylizedNumberedListItemEditor } from './StylizedNumberedListItemEditor';
import {
  createStylizedNumberedListEditorStyles,
  stylizedNumberedListEditorInnerStyles,
} from './stylizedNumberedListEditorStyles';
import {
  createStylizedNumberedListItemFieldName,
  type StylizedNumberedListFieldName,
} from './stylizedNumberedListEditorTypes';
import { TitleLevelSelector } from './TitleLevelSelector';

export interface StylizedNumberedListEditorProps {
  block: StylizedNumberedListBlock;
  onChange: (updates: Partial<StylizedNumberedListBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

function generateUniqueId(): string {
  return crypto.randomUUID();
}

function createEmptyContent(): JSONContent {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

export function StylizedNumberedListEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: StylizedNumberedListEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const [focusedField, setFocusedField] = useState<StylizedNumberedListFieldName | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const titleLevel = block.titleLevel ?? 'h3';
  const resolvedListStyle = block.listStyle ?? 'numbered';
  const listStyles = useMemo(
    () => createStylizedListStyles(tokens, isMobile, isSmallMobile, titleLevel, resolvedListStyle),
    [tokens, isMobile, isSmallMobile, titleLevel, resolvedListStyle]
  );
  const isFocused = focusedField !== null;
  const isBulleted = resolvedListStyle === 'bulleted';
  // Layout values mirror the renderer's local constants (see styles/stylizedList.ts).
  // Inlined as part of the Phase 4 component-token flatten.
  const markerSize = isBulleted
    ? (isSmallMobile ? '10px' : '16px')   // bulletBadgeSize (NA-205: 10px ≤576px)
    : (isMobile ? '30px' : '36px');       // numberedBadgeSize
  const contentGap = '20px';          // stylizedList.contentGap (same on desktop and mobile)
  const toolbarOffset = `calc(${markerSize} + ${contentGap})`;
  const editorStyles = useMemo(
    () => createStylizedNumberedListEditorStyles(tokens, listStyles, isFocused),
    [isFocused, listStyles, tokens]
  );

  useEffect(() => {
    if (autoFocus) {
      titleInputRef.current?.focus();
    }
  }, [autoFocus]);

  const handleFieldFocus = useCallback((fieldName: StylizedNumberedListFieldName) => {
    setFocusedField(fieldName);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange({ title: event.target.value });
    },
    [onChange]
  );

  const handleItemContentChange = useCallback(
    (itemId: string, content: JSONContent) => {
      const updatedItems = block.items.map((item) =>
        item.id === itemId ? { ...item, content } : item
      );
      onChange({ items: updatedItems });
    },
    [block.items, onChange]
  );

  const handleAddItem = useCallback(() => {
    const newItem: NumberedListItem = {
      id: generateUniqueId(),
      content: createEmptyContent(),
    };

    onChange({ items: [...block.items, newItem] });
  }, [block.items, onChange]);

  const handleDeleteItem = useCallback(
    (itemId: string) => {
      if (block.items.length <= 1) {
        return;
      }

      onChange({ items: block.items.filter((item) => item.id !== itemId) });

      if (focusedField === createStylizedNumberedListItemFieldName(itemId)) {
        setFocusedField(null);
      }
    },
    [block.items, focusedField, onChange]
  );

  const handleStyleToggle = useCallback(() => {
    onChange({
      listStyle: resolvedListStyle === 'bulleted' ? 'numbered' : 'bulleted',
    });
  }, [onChange, resolvedListStyle]);

  const handleTitleLevelChange = useCallback(
    (nextTitleLevel: StylizedListTitleLevel) => {
      onChange({ titleLevel: nextTitleLevel });
    },
    [onChange]
  );

  const handleButtonMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <div ref={containerRef} style={editorStyles.container}>
      <style>{stylizedNumberedListEditorInnerStyles}</style>

      {!translationMode ? <div style={editorStyles.topToolbar}>
        <button
          onClick={handleStyleToggle}
          onMouseDown={handleButtonMouseDown}
          style={editorStyles.styleToggleButton}
          type="button"
          title={isBulleted ? 'Switch to numbered list' : 'Switch to bulleted list'}
          aria-label={isBulleted ? 'Switch to numbered list' : 'Switch to bulleted list'}
        >
          <span style={editorStyles.styleToggleIcon}>{isBulleted ? '•' : '1.'}</span>
          <span style={editorStyles.styleToggleLabel}>
            {isBulleted ? 'Bulleted' : 'Numbered'}
          </span>
          <span style={editorStyles.styleToggleArrow}>⇄</span>
        </button>

        <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => handleTitleLevelChange(level as StylizedListTitleLevel)}
          tokens={tokens}
        />
      </div> : null}

      <input
        ref={titleInputRef}
        type="text"
        value={block.title ?? ''}
        onChange={handleTitleChange}
        onFocus={() => handleFieldFocus('title')}
        onBlur={handleFieldBlur}
        placeholder="Optional list title"
        style={editorStyles.titleInput}
      />

      <div style={editorStyles.itemsList}>
        {block.items.map((item, index) => {
          const itemFieldName = createStylizedNumberedListItemFieldName(item.id);

          return (
            <StylizedNumberedListItemEditor
              key={item.id}
              item={item}
              index={index}
              isOnlyItem={block.items.length === 1}
              isFocused={focusedField === itemFieldName}
              isBulleted={isBulleted}
              onFocus={() => handleFieldFocus(itemFieldName)}
              onBlur={handleFieldBlur}
              onContentChange={(content) => handleItemContentChange(item.id, content)}
              onDelete={() => handleDeleteItem(item.id)}
              autoFocus={false}
              containerRef={containerRef}
              listStyles={listStyles}
              editorStyles={editorStyles}
              toolbarOffset={toolbarOffset}
              translationMode={translationMode}
            />
          );
        })}
      </div>

      {!translationMode ? <button
        onClick={handleAddItem}
        onMouseDown={handleButtonMouseDown}
        style={editorStyles.addButton}
        type="button"
      >
        + Add Item
      </button> : null}
    </div>
  );
}

StylizedNumberedListEditor.displayName = 'StylizedNumberedListEditor';
